import { useMemo, useState } from 'react';
import type { EntryRow, Project, Totals } from '../../shared/types';
import { formatDuration } from '../lib/duration';
import { formatDayMonth, formatINR, formatWeekday } from '../lib/format';
import { WorkTypeBadge } from './WorkTypeBadge';
import { EmptyState } from './ui';
import { EntryCards } from './EntryCards';
import { EntrySheet } from './EntrySheet';
import {
  EntryFormCells,
  draftFromEntry,
  emptyDraft,
  isComplete,
  type EntryDraft,
} from './EntryFormRow';

export interface TimesheetTableProps {
  entries: EntryRow[];
  projects: Project[];
  totals: Totals;
  showAmounts: boolean;
  /** Year views collapse into expandable month sections. */
  groupByMonth: boolean;
  defaultDate: string;
  defaultProjectId: string;
  busy: boolean;
  onCreate: (draft: EntryDraft) => Promise<void>;
  onUpdate: (id: string, draft: EntryDraft) => Promise<void>;
  onDelete: (entry: EntryRow) => Promise<void>;
}

const monthLabel = (key: string) =>
  new Date(`${key}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

type Sheet = { mode: 'create' } | { mode: 'edit'; id: string };

export function TimesheetTable(props: TimesheetTableProps) {
  const {
    entries,
    projects,
    totals,
    showAmounts,
    groupByMonth,
    defaultDate,
    defaultProjectId,
    busy,
    onCreate,
    onUpdate,
    onDelete,
  } = props;

  const [draft, setDraft] = useState<EntryDraft>(() => emptyDraft(defaultDate, defaultProjectId));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EntryDraft | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [sheetDraft, setSheetDraft] = useState<EntryDraft>(() => emptyDraft(defaultDate, defaultProjectId));

  const columnCount = showAmounts ? 8 : 7;

  const months = useMemo(() => {
    if (!groupByMonth) return null;
    const map = new Map<string, { entries: EntryRow[]; minutes: number; amount: number }>();
    for (const entry of entries) {
      const key = entry.work_date.slice(0, 7);
      const bucket = map.get(key) ?? { entries: [], minutes: 0, amount: 0 };
      bucket.entries.push(entry);
      bucket.minutes += entry.minutes;
      bucket.amount += entry.amount_inr;
      map.set(key, bucket);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries, groupByMonth]);

  const toggleMonth = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const submitNew = async () => {
    await onCreate(draft);
    // Keep date and project so logging several entries for a day stays quick.
    setDraft({ ...emptyDraft(draft.work_date, draft.project_id), work_type: draft.work_type });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const submitEdit = async () => {
    if (!editingId || !editDraft || !isComplete(editDraft)) return;
    await onUpdate(editingId, editDraft);
    cancelEdit();
  };

  const submitSheet = async () => {
    if (!sheet) return;
    if (sheet.mode === 'create') await onCreate(sheetDraft);
    else await onUpdate(sheet.id, sheetDraft);
    setSheet(null);
  };

  const openSheet = (next: Sheet, initial: EntryDraft) => {
    setSheetDraft(initial);
    setSheet(next);
  };

  const renderRow = (entry: EntryRow) => {
    if (editingId === entry.id && editDraft) {
      return (
        <tr key={entry.id} className="bg-[var(--color-blue-50)]">
          <EntryFormCells
            draft={editDraft}
            projects={projects}
            showAmounts={showAmounts}
            onChange={setEditDraft}
            onSubmit={() => void submitEdit()}
            onCancel={cancelEdit}
            submitting={busy}
            submitLabel="Save"
          />
        </tr>
      );
    }

    return (
      <tr key={entry.id} className="group">
        <td className="nums whitespace-nowrap">
          <span className="font-semibold">{formatDayMonth(entry.work_date)}</span>
          <span className="ml-1.5 text-[11px] text-[var(--color-ink-muted)]">
            {formatWeekday(entry.work_date)}
          </span>
        </td>
        <td>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: entry.project_color }}
              aria-hidden
            />
            {entry.project_name}
          </span>
        </td>
        <td className="min-w-[14rem]">{entry.title}</td>
        <td>
          <WorkTypeBadge type={entry.work_type} />
        </td>
        <td className="num-cell font-semibold whitespace-nowrap">{formatDuration(entry.minutes)}</td>
        {showAmounts && <td className="num-cell whitespace-nowrap">{formatINR(entry.amount_inr)}</td>}
        <td className="max-w-[20rem] text-[var(--color-ink-soft)]">{entry.remark}</td>
        <td>
          <div className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button
              type="button"
              className="btn btn-ghost !px-2 !py-1 text-[12px]"
              onClick={() => {
                setEditingId(entry.id);
                setEditDraft(draftFromEntry(entry));
              }}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-ghost !px-2 !py-1 text-[12px] !text-[var(--color-coral-600)]"
              onClick={() => {
                if (confirm(`Delete "${entry.title}"? This cannot be undone.`)) void onDelete(entry);
              }}
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      {/* Desktop: an editable table with the totals pinned as the last row. */}
      <div className="table-shell hidden md:block">
        <div className="table-scroll">
          <table className="sheet min-w-[64rem]">
            <thead>
              <tr>
                <th>Date</th>
                <th>Project</th>
                <th>Title</th>
                <th>Type of work</th>
                <th className="!text-right">Hours</th>
                {showAmounts && <th className="!text-right">Amount</th>}
                <th>Remark</th>
                <th className="!text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="!p-0">
                    <EmptyState
                      title="Nothing logged yet"
                      description="Add your first row below — pick a date, project and how long it took."
                    />
                  </td>
                </tr>
              )}

              {months
                ? months.map(([key, bucket]) => {
                    const open = !collapsed.has(key);
                    return [
                      <tr key={key} className="!bg-[var(--color-cyan-100)]">
                        <td colSpan={4}>
                          <button
                            type="button"
                            className="flex items-center gap-2 font-display text-[14px] font-semibold"
                            aria-expanded={open}
                            onClick={() => toggleMonth(key)}
                          >
                            <span aria-hidden className="text-[10px]">
                              {open ? '▼' : '▶'}
                            </span>
                            {monthLabel(key)}
                            <span className="text-[11px] font-normal text-[var(--color-ink-muted)]">
                              {bucket.entries.length} {bucket.entries.length === 1 ? 'entry' : 'entries'}
                            </span>
                          </button>
                        </td>
                        <td className="num-cell font-display font-semibold whitespace-nowrap">
                          {formatDuration(bucket.minutes)}
                        </td>
                        {showAmounts && (
                          <td className="num-cell font-display font-semibold whitespace-nowrap">
                            {formatINR(bucket.amount)}
                          </td>
                        )}
                        <td colSpan={2} />
                      </tr>,
                      ...(open ? bucket.entries.map(renderRow) : []),
                    ];
                  })
                : entries.map(renderRow)}

              <tr className="!bg-white">
                <EntryFormCells
                  draft={draft}
                  projects={projects}
                  showAmounts={showAmounts}
                  onChange={setDraft}
                  onSubmit={() => void submitNew()}
                  submitting={busy}
                  submitLabel="Add"
                />
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={4}>
                  TOTAL
                  <span className="ml-2 font-sans text-[12px] font-medium text-[var(--color-ink-muted)]">
                    {totals.entry_count} {totals.entry_count === 1 ? 'entry' : 'entries'}
                  </span>
                </td>
                <td className="num-cell whitespace-nowrap">{formatDuration(totals.minutes)}</td>
                {showAmounts && <td className="num-cell whitespace-nowrap">{formatINR(totals.amount_inr)}</td>}
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Mobile: stacked cards, editing in a sheet, totals pinned to the viewport. */}
      <div className="md:hidden">
        <button
          type="button"
          className="btn btn-primary mb-3 w-full"
          onClick={() => openSheet({ mode: 'create' }, emptyDraft(defaultDate, defaultProjectId))}
        >
          Add entry
        </button>

        {entries.length === 0 ? (
          <div className="table-shell">
            <EmptyState
              title="Nothing logged yet"
              description="Tap “Add entry” to log your first row for this period."
            />
          </div>
        ) : months ? (
          <div className="space-y-4">
            {months.map(([key, bucket]) => {
              const open = !collapsed.has(key);
              return (
                <section key={key}>
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => toggleMonth(key)}
                    className="mb-2 flex w-full items-center gap-2 rounded-xl bg-[var(--color-cyan-100)] px-3 py-2 text-left font-display text-[14px] font-semibold"
                  >
                    <span aria-hidden className="text-[10px]">
                      {open ? '▼' : '▶'}
                    </span>
                    {monthLabel(key)}
                    <span className="nums ml-auto">{formatDuration(bucket.minutes)}</span>
                  </button>
                  {open && (
                    <EntryCards
                      entries={bucket.entries}
                      showAmounts={showAmounts}
                      onEdit={(entry) => openSheet({ mode: 'edit', id: entry.id }, draftFromEntry(entry))}
                      onDelete={(entry) => void onDelete(entry)}
                    />
                  )}
                </section>
              );
            })}
          </div>
        ) : (
          <EntryCards
            entries={entries}
            showAmounts={showAmounts}
            onEdit={(entry) => openSheet({ mode: 'edit', id: entry.id }, draftFromEntry(entry))}
            onDelete={(entry) => void onDelete(entry)}
          />
        )}

        <div className="sticky bottom-0 z-20 mt-3 -mx-5 border-t-2 border-[var(--color-gold-400)] bg-[var(--color-gold-100)] px-5 py-3">
          <div className="flex items-baseline justify-between">
            <span className="font-display text-[15px] font-bold">TOTAL</span>
            <span className="nums font-display text-[17px] font-bold">
              {formatDuration(totals.minutes)}
              {showAmounts && (
                <span className="ml-3 text-[15px]">{formatINR(totals.amount_inr)}</span>
              )}
            </span>
          </div>
        </div>
      </div>

      {sheet && (
        <EntrySheet
          title={sheet.mode === 'create' ? 'Add entry' : 'Edit entry'}
          draft={sheetDraft}
          projects={projects}
          submitting={busy}
          onChange={setSheetDraft}
          onSubmit={() => void submitSheet()}
          onClose={() => setSheet(null)}
        />
      )}
    </>
  );
}
